/**
 * Alpha Workwear header behaviour.
 *
 * Four independent concerns, each written so the header still works if this file
 * never executes:
 *
 *   NAV      - the mega panels have a CSS-only hover/focus-within fallback. This
 *              file adds `is-js` to the nav, which switches that fallback off and
 *              hands state to `.is-open`. Without that handover a panel closed
 *              with Escape would immediately re-open under a stationary pointer.
 *   DRAWER   - opened only from a <button>, so nothing is stranded when JS is
 *              absent; a <noscript> rule in the section unhides the nav row on
 *              small screens for that case.
 *   STICKY   - the main row is pinned with position: fixed and its height handed
 *              to a spacer at the same instant, so the document never changes
 *              height and nothing shifts.
 *   CART     - Dawn's cart JS replaces the innerHTML of #cart-icon-bubble with
 *              its own markup on every cart change. Rather than fight it, we let
 *              it land, read the count out of it, and restore our own markup.
 */

(function () {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  // getClientRects() rather than offsetParent: the drawer panel is position:
  // fixed, for which offsetParent is null even when it is plainly on screen.
  function focusables(root) {
    return Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return !el.hasAttribute('disabled') && el.getClientRects().length > 0;
    });
  }

  function trapTab(event, scope) {
    if (event.key !== 'Tab') return;
    const items = focusables(scope);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  class AlphaHeader extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-alpha-main]');
      this.spacer = this.querySelector('[data-alpha-spacer]');
      this.nav = this.querySelector('[data-alpha-nav]');
      this.drawer = this.querySelector('[data-alpha-drawer]');
      this.drawerPanel = this.querySelector('[data-alpha-drawer-panel]');
      this.drawerIsOpen = false;

      this.setupNav();
      this.setupDrawer();
      this.setupSticky();
      this.setupCartCount();
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
      if (this.onDocKey) document.removeEventListener('keydown', this.onDocKey);
      if (this.cartObserver) this.cartObserver.disconnect();
      if (this.sizeObserver) this.sizeObserver.disconnect();
      document.body.classList.remove('alpha-drawer-open');
    }

    /* ------------------------------------------------------------------ nav */

    setupNav() {
      if (!this.nav) return;

      this.navItems = Array.prototype.slice
        .call(this.nav.querySelectorAll('[data-alpha-nav-item]'))
        .filter(function (item) {
          return item.querySelector('[data-alpha-panel]');
        });

      // Claim the nav even when nothing is expandable, so the CSS fallback is
      // consistently off wherever this file ran.
      this.nav.classList.add('is-js');
      if (!this.navItems.length) return;

      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const self = this;

      this.navItems.forEach(function (item) {
        const toggle = item.querySelector('[data-alpha-nav-toggle]');
        const link = item.querySelector('.alpha-nav__link');

        if (canHover) {
          item.addEventListener('mouseenter', function () {
            self.openPanel(item, false);
          });
          item.addEventListener('mouseleave', function () {
            self.closePanel(item);
          });
        }

        if (toggle) {
          toggle.addEventListener('click', function (event) {
            event.preventDefault();
            if (item.classList.contains('is-open')) {
              self.closePanel(item);
            } else {
              self.openPanel(item, true);
            }
          });
        }

        // Opening on focus is what makes the panel reachable by keyboard at all;
        // the toggle button is what makes Enter/Space explicit. The guard exists
        // because Escape returns focus to this same link, and without it the
        // panel would re-open in the same tick it was dismissed.
        if (link) {
          link.addEventListener('focus', function () {
            if (self.suppressFocusOpen) return;
            self.openPanel(item, false);
          });
        }

        item.addEventListener('focusout', function (event) {
          if (item.contains(event.relatedTarget)) return;
          if (canHover && item.matches(':hover')) return;
          self.closePanel(item);
        });

        item.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') {
            event.stopPropagation();
            self.closePanel(item);
            if (link) {
              self.suppressFocusOpen = true;
              link.focus();
              self.suppressFocusOpen = false;
            }
            return;
          }

          if (!item.classList.contains('is-open')) return;

          if (event.key === 'ArrowDown' && (event.target === link || event.target === toggle)) {
            const inPanel = focusables(item.querySelector('[data-alpha-panel]'));
            if (inPanel.length) {
              event.preventDefault();
              inPanel[0].focus();
            }
            return;
          }

          // Tab is deliberately NOT trapped here. This is a menu, not a modal:
          // trapping it would make every item after the first unreachable by
          // keyboard. The focusout handler above closes the panel as Tab leaves,
          // so the natural order is link -> toggle -> panel -> next item.
        });
      });

      // Left/right walk the top level and close whatever is open - the usual
      // menubar shortcut, on top of plain Tab.
      this.nav.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const links = Array.prototype.slice.call(self.nav.querySelectorAll('.alpha-nav__link'));
        const index = links.indexOf(document.activeElement);
        if (index === -1) return;
        event.preventDefault();
        self.closeAllPanels();
        const step = event.key === 'ArrowRight' ? 1 : -1;
        links[(index + step + links.length) % links.length].focus();
      });

      this.onDocClick = function (event) {
        if (self.nav.contains(event.target)) return;
        self.closeAllPanels();
      };
      document.addEventListener('click', this.onDocClick);

      // A panel opened by hover has no focus inside it, so the per-item Escape
      // handler would never see the key.
      this.onDocKey = function (event) {
        if (event.key !== 'Escape') return;
        self.closeAllPanels();
      };
      document.addEventListener('keydown', this.onDocKey);
    }

    openPanel(item, moveFocus) {
      if (item.classList.contains('is-open')) return;
      this.closeAllPanels();
      item.classList.add('is-open');
      /*
       * Tell the PAGE a mega panel is open.
       *
       * The panel is absolutely positioned out of the header and falls across
       * whatever section is beneath it. On a collection page that is a sticky
       * filter toolbar, and no arrangement of z-index settled which of the two
       * painted on top - the toolbar kept drawing through the open menu. Rather
       * than keep tuning numbers across two stylesheets, the page is told to get
       * out of the way for as long as the menu is open. See
       * component-alpha-collection.css for the one rule that consumes this.
       */
      document.documentElement.classList.add('alpha-nav-open');
      const toggle = item.querySelector('[data-alpha-nav-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');

      if (moveFocus) {
        const inPanel = focusables(item.querySelector('[data-alpha-panel]'));
        if (inPanel.length) inPanel[0].focus();
      }
    }

    closePanel(item) {
      if (!item.classList.contains('is-open')) return;
      item.classList.remove('is-open');
      // Only clear it once NO panel is left open - closing one while another is
      // opening (a pointer moving along the nav) must not flash the toolbar back.
      if (!document.querySelector('.alpha-nav__item.is-open')) {
        document.documentElement.classList.remove('alpha-nav-open');
      }
      const toggle = item.querySelector('[data-alpha-nav-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    closeAllPanels() {
      const self = this;
      (this.navItems || []).forEach(function (item) {
        self.closePanel(item);
      });
    }

    /* --------------------------------------------------------------- drawer */

    setupDrawer() {
      if (!this.drawer || !this.drawerPanel) return;
      const self = this;

      this.drawerOpeners = Array.prototype.slice.call(this.querySelectorAll('[data-alpha-drawer-open]'));
      this.drawerOpeners.forEach(function (button) {
        button.addEventListener('click', function () {
          self.openDrawer(button);
        });
      });

      Array.prototype.slice.call(this.querySelectorAll('[data-alpha-drawer-close]')).forEach(function (el) {
        el.addEventListener('click', function () {
          self.closeDrawer();
        });
      });

      this.drawer.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          self.closeDrawer();
          return;
        }
        trapTab(event, self.drawerPanel);
      });
    }

    openDrawer(trigger) {
      this.drawerReturnFocus = trigger || document.activeElement;
      this.drawerIsOpen = true;
      // The drawer covers the viewport; a pinned main row would sit on top of it.
      this.unstick(true);
      this.drawer.classList.add('is-open');
      this.drawerOpeners.forEach(function (button) {
        button.setAttribute('aria-expanded', 'true');
      });
      document.body.classList.add('alpha-drawer-open');

      // preventScroll: the panel is still translated off-screen for one frame,
      // and focusing into it would otherwise scroll it into view.
      const first = focusables(this.drawerPanel);
      if (first.length) first[0].focus({ preventScroll: true });
    }

    closeDrawer() {
      if (!this.drawerIsOpen) return;
      this.drawerIsOpen = false;
      this.drawer.classList.remove('is-open');
      this.drawerOpeners.forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
      });
      document.body.classList.remove('alpha-drawer-open');
      if (this.drawerReturnFocus && this.drawerReturnFocus.focus) this.drawerReturnFocus.focus();
    }

    /* --------------------------------------------------------------- sticky */

    setupSticky() {
      if (!this.main || !this.spacer) return;
      this.stickyEnabled = this.dataset.sticky === 'true';
      this.isFixed = false;
      this.isStuck = false;
      this.lastScroll = window.pageYOffset;

      const self = this;
      this.measure();

      this.onScroll = function () {
        if (self.scrollQueued) return;
        self.scrollQueued = true;
        window.requestAnimationFrame(function () {
          self.scrollQueued = false;
          self.handleScroll();
        });
      };
      this.onResize = function () {
        // The drawer is display:none from 990px up. Resizing past that while it
        // is open would leave the body scroll lock on with nothing on screen to
        // dismiss, and the page would be stuck.
        if (self.drawerIsOpen && window.innerWidth >= 990) self.closeDrawer();
        self.measure();
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      // The main row changes height when the logo image finally decodes; without
      // this the spacer would be measured against a collapsed row.
      if ('ResizeObserver' in window) {
        this.sizeObserver = new ResizeObserver(function () {
          self.measure();
        });
        this.sizeObserver.observe(this.main);
      }

      this.handleScroll();
    }

    measure() {
      if (!this.main) return;
      this.mainHeight = this.main.offsetHeight;
      this.style.setProperty('--alpha-header-main-h', this.mainHeight + 'px');
      // The nav row pins directly beneath the main row, so both its own offset
      // and the spacer that replaces them depend on this height. Measured rather
      // than assumed: the row's height changes with the viewport as the links
      // give up padding, and it is display:none below 990px.
      var navRow = this.querySelector('.alpha-header__nav');
      var navH = navRow && window.getComputedStyle(navRow).display !== 'none' ? navRow.offsetHeight : 0;
      this.navHeight = navH;
      this.style.setProperty('--alpha-header-nav-h', navH + 'px');
      if (!this.isFixed) {
        this.releasePoint = window.pageYOffset + this.getBoundingClientRect().bottom;
      }
      // Other Dawn components read this to size themselves under the header.
      document.documentElement.style.setProperty('--header-height', this.offsetHeight + 'px');
    }

    handleScroll() {
      if (!this.stickyEnabled || this.drawerIsOpen) return;
      const y = Math.max(window.pageYOffset, 0);
      const goingUp = y < this.lastScroll;
      this.lastScroll = y;

      if (y <= this.releasePoint) {
        this.unstick(true);
      } else if (goingUp && y > this.releasePoint + 60) {
        this.stick();
      } else if (!goingUp) {
        this.unstick(false);
      }
    }

    stick() {
      if (this.isStuck) return;
      const self = this;
      window.clearTimeout(this.releaseTimer);

      if (!this.isFixed) {
        this.isFixed = true;
        this.classList.add('is-fixed');
      }
      // One frame between fixing and sliding down, or the transform has no
      // starting value to animate from and the row simply appears.
      window.requestAnimationFrame(function () {
        if (!self.isFixed) return;
        self.isStuck = true;
        self.classList.add('is-stuck');
      });
    }

    unstick(immediate) {
      if (!this.isFixed && !this.isStuck) return;
      const self = this;
      window.clearTimeout(this.releaseTimer);

      this.isStuck = false;
      this.classList.remove('is-stuck');

      if (immediate) {
        this.isFixed = false;
        this.classList.remove('is-fixed');
        return;
      }

      // Keep the spacer in place until the slide-up finishes, otherwise the row
      // rejoins the flow mid-animation and the page jumps by its height.
      this.releaseTimer = window.setTimeout(function () {
        if (self.isStuck) return;
        self.isFixed = false;
        self.classList.remove('is-fixed');
      }, 340);
    }

    /* ----------------------------------------------------------------- cart */

    setupCartCount() {
      this.cartLink = this.querySelector('#cart-icon-bubble');
      if (!this.cartLink) return;

      // Snapshot our own markup before any cart update can overwrite it.
      this.cartMarkup = this.cartLink.cloneNode(true);

      const self = this;
      this.cartObserver = new MutationObserver(function () {
        self.restoreCartMarkup();
      });
      this.cartObserver.observe(this.cartLink, { childList: true, subtree: true });
    }

    restoreCartMarkup() {
      // Our markup is intact - this mutation was ours, or nothing relevant.
      if (this.cartLink.querySelector('[data-alpha-cart-count]')) return;

      const bubble = this.cartLink.querySelector('.cart-count-bubble');
      const digits = bubble ? bubble.textContent.match(/\d+/) : null;
      const count = digits ? parseInt(digits[0], 10) : 0;

      // Dawn renders the pluralised, translated count string server side; reuse
      // it rather than rebuilding plural rules in the browser. Two queries, not
      // one comma-separated selector: querySelector returns the first match in
      // DOCUMENT order, and Dawn's bare "Cart" label precedes the count string.
      const spoken =
        this.cartLink.querySelector('.cart-count-bubble .visually-hidden') ||
        this.cartLink.querySelector('.visually-hidden');
      const spokenText = spoken ? spoken.textContent.trim() : '';

      this.cartObserver.disconnect();

      const fresh = this.cartMarkup.cloneNode(true);
      this.cartLink.replaceChildren.apply(this.cartLink, Array.prototype.slice.call(fresh.childNodes));

      const badge = this.cartLink.querySelector('[data-alpha-cart-count]');
      if (badge) {
        badge.textContent = count > 99 ? '99+' : String(count);
        if (count > 0) {
          badge.removeAttribute('hidden');
        } else {
          badge.setAttribute('hidden', '');
        }
      }

      const label = this.cartLink.querySelector('[data-alpha-cart-text]');
      if (label && spokenText) label.textContent = spokenText;

      this.cartObserver.observe(this.cartLink, { childList: true, subtree: true });
    }
  }

  if (!customElements.get('alpha-header')) {
    customElements.define('alpha-header', AlphaHeader);
  }
})();
